import { DatePicker, Drawer, Select, Space, Table, Tag, Typography } from 'antd';
import moment from 'moment';
import React from 'react';
import { PricingAPI } from '../../../../../controller/PricingAPI';
import { complianceStateMeta, showRequestError } from '../todoHelpers';

const EMPTY_SUMMARY = { expected: 0, completed: 0, ignored: 0, pending: 0, missing: 0 };

// 「某段期間該做的事做了沒」。漏做的例行工作刻意不留在執行者的清單上，
// 所以這裡是唯一能回答「誰沒做」的地方。
// 父層以條件渲染掛載，每次開啟都重新掛載、區間回到本月 1 日至今：這個抽屜是
// 「看一眼就關」的工具，不是工作區，每次開啟都是同一個可預期的預設比較好。
function TodoComplianceDrawer({ open, canFilterDepartment, onClose }) {
    // 預設本月 1 日至今：主管最常問的就是「這個月到目前為止」
    const [range, setRange] = React.useState([moment().startOf('month'), moment()]);
    const [departmentId, setDepartmentId] = React.useState(null);
    const [departments, setDepartments] = React.useState([]);
    const [summary, setSummary] = React.useState(EMPTY_SUMMARY);
    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (!open || !canFilterDepartment) {
            return;
        }
        PricingAPI.departments.options()
            .then((response) => setDepartments(response.data))
            .catch(showRequestError);
    }, [open, canFilterDepartment]);

    React.useEffect(() => {
        if (!open || !range || !range[0] || !range[1]) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        PricingAPI.todos.compliance({
            from: range[0].format('YYYY-MM-DD'),
            to: range[1].format('YYYY-MM-DD'),
            department_id: departmentId || undefined,
        }).then((response) => {
            if (cancelled) {
                return;
            }
            setSummary(response.data.summary || EMPTY_SUMMARY);
            setRows(response.data.rows || []);
        }).catch((error) => {
            if (!cancelled) {
                showRequestError(error);
                setRows([]);
                setSummary(EMPTY_SUMMARY);
            }
        }).finally(() => {
            if (!cancelled) {
                setLoading(false);
            }
        });
        return () => { cancelled = true; };
    }, [open, range, departmentId]);

    const columns = [
        {
            title: '日期',
            dataIndex: 'date',
            width: 110,
            render: (value) => <span style={{ whiteSpace: 'nowrap' }}>{value}</span>,
        },
        { title: '主旨', dataIndex: 'subject', width: 240 },
        {
            title: '負責人',
            width: 120,
            render: (_, row) => (row.assignee ? row.assignee.name : <Typography.Text type="secondary">—</Typography.Text>),
        },
        {
            title: '部門',
            width: 120,
            render: (_, row) => (row.department ? row.department.name : <Typography.Text type="secondary">—</Typography.Text>),
        },
        {
            title: '狀態',
            dataIndex: 'state',
            width: 100,
            render: (state) => {
                const meta = complianceStateMeta(state);
                return <Tag color={meta.color}>{meta.label}</Tag>;
            },
        },
    ];

    return (
        <Drawer
            title="合規檢視"
            visible={open}
            onClose={onClose}
            width="min(1040px, 100vw)"
            destroyOnClose
        >
            <Space style={{ marginBottom: 12 }} wrap>
                <DatePicker.RangePicker
                    value={range}
                    onChange={setRange}
                    allowClear={false}
                    disabledDate={(date) => date && date.isAfter(moment(), 'day')}
                />
                {canFilterDepartment && (
                    <Select
                        allowClear
                        placeholder="全部部門"
                        style={{ width: 160 }}
                        value={departmentId}
                        onChange={setDepartmentId}
                        options={departments.map((d) => ({ value: d.id, label: d.name }))}
                    />
                )}
            </Space>
            <Space style={{ marginBottom: 12 }} wrap>
                <Tag>該做 {summary.expected}</Tag>
                <Tag color="green">已完成 {summary.completed}</Tag>
                <Tag color="blue">未結案 {summary.pending}</Tag>
                <Tag>已略過 {summary.ignored}</Tag>
                <Tag color="red">沒做 {summary.missing}</Tag>
            </Space>
            <Table
                rowKey={(row) => `${row.template_id}-${row.date}`}
                loading={loading}
                dataSource={rows}
                columns={columns}
                pagination={{ pageSize: 50, showSizeChanger: false }}
                scroll={{ x: 690 }}
            />
        </Drawer>
    );
}

export default TodoComplianceDrawer;
